import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n';

const ROLL_TICK_MS = 70;
const ROLL_STEPS = 12;

export type BonusWinnerRollTarget = { playerName: string; points: number };

type Props = {
  target: BonusWinnerRollTarget;
  /** Stable across renders when possible (e.g. memoized from standings). */
  namePool: string[];
};

function pickRandomFromPool(pool: string[], avoid?: string): string {
  if (pool.length === 0) return '';
  if (pool.length === 1) return pool[0];
  let n: string;
  let guard = 0;
  do {
    n = pool[Math.floor(Math.random() * pool.length)] ?? '';
    guard++;
  } while (avoid && n === avoid && guard < 24);
  return n;
}

/**
 * Bingo-style name cycling into the final bonus winner; re-rolls when a late
 * submission changes who won. Same winner with only points updated skips the reel.
 */
export const BonusWinnerRoll: React.FC<Props> = ({ target, namePool }) => {
  const { t } = useTranslation();
  const pool = useMemo(() => {
    const names = Array.from(new Set(namePool.map((n) => (n ?? '').trim()).filter(Boolean)));
    return names.length > 0 ? names : [target.playerName];
  }, [namePool, target.playerName]);

  const [displayName, setDisplayName] = useState(() => pickRandomFromPool(pool, target.playerName));
  const [displayPoints, setDisplayPoints] = useState(target.points);
  const displayNameRef = useRef(displayName);
  displayNameRef.current = displayName;
  const runSeq = useRef(0);

  useEffect(() => {
    const seq = ++runSeq.current;

    if (displayNameRef.current === target.playerName) {
      setDisplayPoints(target.points);
      return () => {
        runSeq.current++;
      };
    }

    let step = 0;
    const iv = window.setInterval(() => {
      if (seq !== runSeq.current) {
        window.clearInterval(iv);
        return;
      }
      step++;
      if (step >= ROLL_STEPS) {
        setDisplayName(target.playerName);
        setDisplayPoints(target.points);
        window.clearInterval(iv);
      } else {
        setDisplayName(pickRandomFromPool(pool, target.playerName));
      }
    }, ROLL_TICK_MS);

    return () => {
      runSeq.current++;
      window.clearInterval(iv);
    };
  }, [target.playerName, target.points, pool]);

  return (
    <>
      <div
        className="bonus-winner-label"
        style={{
          fontSize: 'clamp(1.8rem, 4.5vw, 2.5rem)',
          marginBottom: '0.5rem',
          fontWeight: 600,
        }}
      >
        {t('bonus.winnerTitle')}
      </div>
      <div
        className="bonus-winner-name"
        style={{
          fontSize: 'clamp(2.6rem, 6.5vw, 4rem)',
          fontWeight: 'bold',
          minHeight: '1.2em',
          transition: 'opacity 0.08s ease-out',
        }}
      >
        {displayName}
      </div>
      <div
        style={{
          fontSize: 'clamp(1.5rem, 3.5vw, 2.1rem)',
          fontWeight: 'bold',
          color: '#34D399',
          marginTop: '0.3rem',
        }}
      >
        +{displayPoints} {t('common.pts')}
      </div>
    </>
  );
};
